import {
  ShieldCheckIcon,
  EyeSlashIcon,
  LockClosedIcon,
  ServerIcon,
  DocumentCheckIcon,
  CloudIcon,
  CubeTransparentIcon,
  HandRaisedIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';

export default function TrustCompliancePage() {
  return (
    <div className="bg-white dark:bg-slate-900">
      {/* Hero */}
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-5xl">
            Trust & Compliance Centre
          </h1>
          <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-300">
            At CyberActions, trust is built through transparency, security, and accountability.
            CyberActions Limited is officially certified under the UK National Cyber Security
            Centre (NCSC) Cyber Essentials scheme (Certificate No:{' '}
            <span className="font-mono text-slate-900 dark:text-white">
              17b5fb1d-f76f-4bd1-8aa8-87b622e89971
            </span>
            ).
          </p>
          <p className="mt-4 text-lg leading-8 text-slate-600 dark:text-slate-300">
            This page outlines how we protect customer data, maintain our fully insured
            operational safeguards, align natively with UK regulations, and support organizations
            in achieving an ironclad security posture.
          </p>

          {/* Cyber Essentials certificate badge */}
          <div className="mt-10 flex justify-center">
            <a
              href="/cyber-essentials-certificate.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-5 py-4 shadow-sm hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
              aria-label="View Cyber Essentials Certificate (PDF)"
            >
              <img
                src="/cyber-essentials-badge.png"
                alt="Cyber Essentials Certified"
                className="h-24 w-auto flex-shrink-0"
              />
              <span className="flex flex-col leading-tight text-left">
                <span className="text-sm font-semibold text-slate-900 dark:text-white">
                  Cyber Essentials Certified ✅
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  Cyber Essentials Plus in progress
                </span>
                <span className="text-[11px] text-primary-600 dark:text-primary-400 mt-1">
                  View certificate (PDF) →
                </span>
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* 1. Compliance Framework Alignment */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                <ShieldCheckIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                1. Compliance Framework Alignment
              </h2>
            </div>
            <p className="text-slate-600 dark:text-slate-300">
              CyberActions is designed to align with recognised UK and international security standards.
            </p>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              We support organisations working towards:
            </p>
            <ul className="mt-4 space-y-4">
              <li className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white">Cyber Essentials & Cyber Essentials Plus</h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  Protection against common cyber threats and baseline UK security standards
                </p>
              </li>
              <li className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white">ISO/IEC 27001 Principles</h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  Alignment with Information Security Management System (ISMS) practices, focusing on:
                </p>
                <ul className="mt-2 ml-4 list-disc text-slate-600 dark:text-slate-300 space-y-1">
                  <li>Confidentiality</li>
                  <li>Integrity</li>
                  <li>Availability</li>
                </ul>
              </li>
              <li className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white">UK GDPR & Data Protection Act 2018</h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  We process data in accordance with UK data protection requirements, with a strong emphasis on data minimisation and lawful processing
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* 2. Read-Only Architecture & Data Flow */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                <EyeSlashIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                2. Read-Only Architecture & Data Flow
              </h2>
            </div>
            <p className="text-slate-600 dark:text-slate-300">
              CyberActions is built as a passive, non-intrusive auditing platform.
            </p>
            <div className="mt-6 space-y-4">
              <div className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white">Read-Only Access Only</h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  We cannot modify, delete, or enforce changes within your environment
                </p>
              </div>
              <div className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white">Metadata-Only Processing ("Zero-Payload Policy")</h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  We only analyse configuration data such as:
                </p>
                <ul className="mt-2 ml-4 list-disc text-slate-600 dark:text-slate-300 space-y-1">
                  <li>MFA status</li>
                  <li>Device compliance</li>
                  <li>Sharing settings</li>
                </ul>
                <p className="mt-4 text-slate-600 dark:text-slate-300">
                  We do not access or process:
                </p>
                <ul className="mt-2 ml-4 list-disc text-slate-600 dark:text-slate-300 space-y-1">
                  <li>Email content</li>
                  <li>Documents or files</li>
                  <li>User communications</li>
                </ul>
              </div>
            </div>
            <p className="mt-6 text-slate-600 dark:text-slate-300">
              This architecture significantly reduces risk and ensures safe adoption.
            </p>
          </div>
        </div>
      </div>

      {/* 3. Shared Responsibility Model */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                <HandRaisedIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                3. Shared Responsibility Model
              </h2>
            </div>
            <p className="text-slate-600 dark:text-slate-300">
              CyberActions operates under a shared responsibility model:
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white">CyberActions is responsible for:</h3>
                <ul className="mt-2 ml-4 list-disc text-slate-600 dark:text-slate-300 space-y-1">
                  <li>Securing the platform</li>
                  <li>Protecting processed metadata</li>
                  <li>Ensuring secure integrations</li>
                </ul>
              </div>
              <div className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white">Customers are responsible for:</h3>
                <ul className="mt-2 ml-4 list-disc text-slate-600 dark:text-slate-300 space-y-1">
                  <li>Configuring their Microsoft 365 and Google Workspace environments</li>
                  <li>Acting on security recommendations</li>
                  <li>Maintaining internal compliance policies</li>
                </ul>
              </div>
            </div>
            <p className="mt-6 text-slate-600 dark:text-slate-300">
              CyberActions provides visibility and guidance — not enforcement.
            </p>
          </div>
        </div>
      </div>

      {/* 4. Data Residency & Sovereignty */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                <ServerIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                4. Data Residency & Sovereignty
              </h2>
            </div>
            <p className="text-slate-600 dark:text-slate-300">
              We are designed with UK organisations in mind.
            </p>
            <div className="mt-6 space-y-4">
              <div className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white">Primary Data Location</h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  Customer data is hosted within the United Kingdom
                </p>
              </div>
              <div className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white">Controlled International Processing</h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  Where third-party services are involved, appropriate safeguards (such as contractual protections) are applied to ensure compliance with UK data protection laws
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Integration Security Standards */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                <LockClosedIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                5. Integration Security Standards
              </h2>
            </div>
            <p className="text-slate-600 dark:text-slate-300">
              CyberActions connects securely to your cloud platforms using official APIs and modern authentication protocols.
            </p>

            {/* Microsoft 365 Integration */}
            <div className="mt-8">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                Microsoft 365 Integration
              </h3>
              <div className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                <ul className="space-y-3 text-slate-600 dark:text-slate-300">
                  <li><span className="font-medium text-slate-900 dark:text-white">Authentication:</span> Secure OAuth 2.0 via Microsoft Entra ID</li>
                  <li><span className="font-medium text-slate-900 dark:text-white">API Framework:</span> Microsoft Graph API</li>
                  <li><span className="font-medium text-slate-900 dark:text-white">Permission Level:</span> Read-only access only</li>
                  <li><span className="font-medium text-slate-900 dark:text-white">Credential Security:</span> No passwords are accessed or stored</li>
                  <li><span className="font-medium text-slate-900 dark:text-white">Access Control:</span> Token-based authentication with revocation available at any time</li>
                </ul>
              </div>
            </div>

            {/* Google Workspace Integration */}
            <div className="mt-8">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                Google Workspace Integration
              </h3>
              <div className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
                <ul className="space-y-3 text-slate-600 dark:text-slate-300">
                  <li><span className="font-medium text-slate-900 dark:text-white">Authentication:</span> Secure OAuth 2.0 via Google Identity</li>
                  <li><span className="font-medium text-slate-900 dark:text-white">API Framework:</span> Google Workspace Admin SDK and security APIs</li>
                  <li><span className="font-medium text-slate-900 dark:text-white">Permission Level:</span> Read-only metadata access</li>
                  <li><span className="font-medium text-slate-900 dark:text-white">Credential Security:</span> No passwords are accessed or stored</li>
                  <li><span className="font-medium text-slate-900 dark:text-white">Access Control:</span> Full control via Google Admin Console</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 6. Third-Party Dependencies */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                <CubeTransparentIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                6. Third-Party Dependencies
              </h2>
            </div>
            <p className="text-slate-600 dark:text-slate-300">
              CyberActions relies on trusted third-party platforms to deliver its services.
            </p>
            <div className="mt-6 rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
              <ul className="ml-4 list-disc text-slate-600 dark:text-slate-300 space-y-2">
                <li>Microsoft 365 and Google Workspace APIs</li>
                <li>Cloud infrastructure providers</li>
                <li>Secure email and monitoring services</li>
              </ul>
            </div>
            <p className="mt-6 text-slate-600 dark:text-slate-300">
              Service availability and certain data insights may depend on these providers.
            </p>
          </div>
        </div>
      </div>

      {/* 7. Audit & Evidence Support */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                <DocumentCheckIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                7. Audit & Evidence Support
              </h2>
            </div>
            <p className="text-slate-600 dark:text-slate-300">
              CyberActions provides structured, audit-ready outputs.
            </p>
            <div className="mt-6 rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
              <ul className="ml-4 list-disc text-slate-600 dark:text-slate-300 space-y-2">
                <li>Security posture reports</li>
                <li>Compliance summaries</li>
                <li>Timestamped audit data</li>
              </ul>
            </div>
            <p className="mt-6 text-slate-600 dark:text-slate-300">
              These outputs can be used as supporting evidence for:
            </p>
            <div className="mt-4 rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700">
              <ul className="ml-4 list-disc text-slate-600 dark:text-slate-300 space-y-2">
                <li>Cyber Essentials assessments</li>
                <li>Internal audits</li>
                <li>Client and supplier due diligence</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 8. Trust Principles */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                <CloudIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                8. Trust Principles
              </h2>
            </div>
            <p className="text-slate-600 dark:text-slate-300">
              Our platform is built on the following core principles:
            </p>
            <div className="mt-6 space-y-4">
              {[
                { title: 'Least Privilege', desc: 'Only minimal permissions are requested' },
                { title: 'Transparency', desc: 'Clear visibility into data access and processing' },
                { title: 'Data Minimisation', desc: 'Only essential metadata is processed' },
                { title: 'Customer Control', desc: 'Access can be revoked at any time' },
                { title: 'Security by Design', desc: 'Security is embedded into every layer' },
              ].map((principle) => (
                <div
                  key={principle.title}
                  className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700"
                >
                  <h3 className="font-semibold text-slate-900 dark:text-white">{principle.title}</h3>
                  <p className="mt-2 text-slate-600 dark:text-slate-300">{principle.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 9. Compliance Roadmap */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                <RocketLaunchIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                9. Compliance Roadmap
              </h2>
            </div>
            <p className="text-slate-600 dark:text-slate-300">
              We are committed to continuous improvement and strengthening our assurance posture.
            </p>
            <p className="mt-4 text-slate-600 dark:text-slate-300">Our roadmap includes:</p>
            <div className="mt-6 space-y-4">
              {[
                { title: 'Cyber Essentials Plus', desc: 'Independent technical verification' },
                { title: 'SOC 2 Type I/II', desc: 'Formal assurance over service controls' },
                { title: 'NCSC Cloud Security Principles', desc: 'Alignment with UK government guidance' },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl bg-white dark:bg-slate-800 p-6 shadow-sm border border-slate-200 dark:border-slate-700"
                >
                  <h3 className="font-semibold text-slate-900 dark:text-white">{item.title}</h3>
                  <p className="mt-2 text-slate-600 dark:text-slate-300">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
